import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { setAccessToken, tryRefresh } from "../../lib/api";
import { fetchMe, login as loginRequest, logout as logoutRequest, type SafeUser } from "./api";

interface AuthValue {
  user: SafeUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SafeUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // No refresh token in reach of JS — just try; the httpOnly cookie
        // (if any, from a previous session) carries it.
        if (await tryRefresh()) {
          const { user } = await fetchMe();
          if (!cancelled) setUser(user);
        }
      } catch {
        // no active session — stay logged out
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const bundle = await loginRequest({ email, password });
    setAccessToken(bundle.accessToken);
    setUser(bundle.user);
  }, []);

  const logout = useCallback(() => {
    void logoutRequest().catch(() => {
      // best effort — clear local state regardless
    });
    setAccessToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const { user } = await fetchMe();
    setUser(user);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refreshUser }),
    [user, loading, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth hors AuthProvider");
  return ctx;
}
